/**
 * IdleWarningModal
 *
 * The countdown a bank shows you two minutes before it signs you out. Without
 * it, a 15-minute idle timeout reads as a bug ("it keeps logging me out!")
 * rather than as a security feature; with it, anyone still at their desk gets
 * one click to stay. That pairing is what makes a short timeout tolerable, and
 * it's why every brokerage ships both together.
 *
 * Deliberately NOT dismissable by clicking the backdrop or pressing Escape —
 * a stray click shouldn't silently extend a session. The two ways out are
 * "Stay signed in" and "Sign out now".
 */

import React, { useEffect, useRef } from "react";
import { Icon } from "./brand";

function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function IdleWarningModal({
  secondsRemaining,
  onStay,
  onSignOut,
}: {
  secondsRemaining: number;
  onStay: () => void;
  onSignOut: () => void;
}) {
  const stayRef = useRef<HTMLButtonElement | null>(null);

  // Focus the safe action so a keyboard user can dismiss with Enter. Note the
  // keydown that gets them here also counts as activity in useIdleTimeout, so
  // simply tabbing to the button already resets the clock — the explicit click
  // is for the mouse case and for making the intent visible.
  useEffect(() => {
    stayRef.current?.focus();
  }, []);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-warning-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 10000,  // above StepUpModal (9999) — signing out trumps a step-up prompt
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        animation: "idleFade 0.15s ease",
      }}
    >
      <style>{`@keyframes idleFade { from { opacity: 0; } to { opacity: 1; } }`}</style>
      <div
        style={{
          background: "white",
          borderRadius: "4px",
          padding: "28px 28px 20px",
          width: "min(420px, 90vw)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
          <Icon name="lock" size={20} />
          <h3 id="idle-warning-title" style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#111827" }}>
            Still there?
          </h3>
        </div>

        <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#6B7280", lineHeight: 1.5 }}>
          You'll be signed out automatically because of inactivity. This protects your
          financial information if you step away from your device.
        </p>

        <div
          aria-live="polite"
          style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: "4px",
            padding: "12px",
            marginBottom: "18px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "26px", fontWeight: 800, color: "#DC2626", letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
            {formatCountdown(secondsRemaining)}
          </div>
          <div style={{ fontSize: "11.5px", color: "#B91C1C", marginTop: "2px" }}>
            until you're signed out
          </div>
        </div>

        <button
          ref={stayRef}
          onClick={onStay}
          style={{
            width: "100%",
            padding: "11px 16px",
            background: "var(--brand-primary)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Stay signed in
        </button>
        <button
          onClick={onSignOut}
          style={{
            width: "100%",
            marginTop: "8px",
            padding: "11px 16px",
            background: "white",
            color: "#6B7280",
            border: "1px solid #E5E7EB",
            borderRadius: "4px",
            fontSize: "13.5px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Sign out now
        </button>
      </div>
    </div>
  );
}
